/**
 * One-tap "Take me home" shortcut. Renders only when:
 *   • The user has a labelled "Home" saved place (case-insensitive, also
 *     matches "house"/"apartment"/"apt" — see findHomePlace).
 *   • The user has a usable start location (GPS or manually-set).
 *   • There's no trip in progress yet.
 *
 * The point is to make the most-common destination one tap away, especially
 * for testers who don't want to type a full address on a phone keyboard.
 *
 * Real-world feedback: "Going home shortcut" — the existing saved-places
 * chips are buried inside the TripSearch component and require opening the
 * search first. This surfaces Home on the landing screen, before any
 * interaction.
 */

import { useEffect, useState } from "react";
import { Home } from "lucide-react";
import {
  findHomePlace,
  iconFor,
  type SavedPlace,
} from "@/utils/savedPlaces";

interface Props {
  /** Fires when the user taps the chip. Parent receives the Home place and
   *  is responsible for filling the destination + starting routing. */
  onGoHome: (home: SavedPlace) => void;
  /** Optional subdued state — used while the parent is fetching routes so
   *  the button doesn't accept a second tap. */
  loading?: boolean;
}

export const GoingHomeChip = ({ onGoHome, loading }: Props) => {
  // Hydrate from localStorage on mount. Re-read whenever a saved place
  // changes — same event the existing SavedPlacesChips listens to.
  const [home, setHome] = useState<SavedPlace | undefined>(() => findHomePlace());
  useEffect(() => {
    const refresh = () => setHome(findHomePlace());
    window.addEventListener("saved-places-changed", refresh);
    window.addEventListener("storage", refresh);
    return () => {
      window.removeEventListener("saved-places-changed", refresh);
      window.removeEventListener("storage", refresh);
    };
  }, []);

  if (!home) return null;

  const icon = home.icon ?? iconFor(home.label);

  return (
    <button
      type="button"
      onClick={() => !loading && onGoHome(home)}
      aria-label={`Take me home to ${home.place}`}
      title={home.place}
      disabled={loading}
      className="pointer-events-auto inline-flex items-center gap-2 h-11 pl-3 pr-4 rounded-full bg-pleasant text-primary-foreground shadow-glow active:scale-[0.98] disabled:opacity-60 transition-transform"
    >
      {/* Emoji icon if one was set/derived, otherwise the lucide Home glyph
       *  so the button always reads as "home" regardless of whether the
       *  device renders emoji nicely. */}
      <span className="text-base leading-none" aria-hidden="true">
        {icon || ""}
      </span>
      {!icon && <Home className="w-4 h-4" aria-hidden="true" />}
      <span className="text-sm font-semibold">Take me home</span>
    </button>
  );
};
